import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AdminLogin() {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { password });
      localStorage.setItem("mrc_token", res.data.token);
      toast.success("Welcome back, operator.");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="admin-login-page">
      <div className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/logo.jpg"
              alt="MR Crusher logo"
              className="h-10 w-10 object-contain"
            />
            <span className="font-display font-bold uppercase tracking-tight">
              MR Crusher
            </span>
          </Link>
          <Link
            to="/"
            className="text-sm text-slate-600 hover:text-[#0055FF] transition-colors"
          >
            ← Back to site
          </Link>
        </div>
      </div>

      <div className="flex-1 grid lg:grid-cols-2">
        <div className="hidden lg:flex flex-col justify-between p-16 bg-slate-900 text-white relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-10" />
          <div className="relative">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#5e8aff]">
              Operator Console
            </div>
            <h2 className="mt-4 font-display font-semibold text-4xl tracking-tight">
              Crusher Control Room
            </h2>
            <p className="mt-4 text-slate-400 max-w-md">
              Every email lead. Every contact form. Parsed, organized, ready to
              quote — all in one panel.
            </p>
          </div>
          <div className="relative space-y-3">
            <Bar label="Inbox sync" pct="98%" />
            <Bar label="Lead conversion" pct="42%" />
            <Bar label="Avg. pickup time" pct="64m" tone />
          </div>
        </div>

        <div className="flex items-center justify-center p-8 lg:p-16">
          <form
            onSubmit={onSubmit}
            className="w-full max-w-md bg-white border border-slate-200 p-8 md:p-10"
            data-testid="admin-login-form"
          >
            <div className="h-12 w-12 grid place-items-center bg-[#0055FF] text-white">
              <Lock className="h-5 w-5" />
            </div>
            <h1 className="mt-6 font-display font-semibold text-3xl tracking-tight">
              Admin login
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Restricted area. Enter the operator password to view leads.
            </p>

            <label className="block mt-8 text-xs font-medium uppercase tracking-widest text-slate-500">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
              data-testid="admin-password-input"
              className="mt-2 w-full bg-white border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#0055FF] focus:border-[#0055FF]"
            />

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#0055FF] hover:bg-[#003ECE] disabled:opacity-60 text-white px-8 py-4 font-medium rounded-full transition-colors"
            >
              {loading ? "Authenticating..." : "Enter dashboard"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Bar({ label, pct, tone }) {
  return (
    <div>
      <div className="flex justify-between text-xs uppercase tracking-widest">
        <span className="text-slate-400">{label}</span>
        <span className="font-mono-plex text-white">{pct}</span>
      </div>
      <div className="mt-2 h-1 bg-slate-700 relative overflow-hidden">
        <div
          className={tone ? "h-full bg-[#5e8aff] w-2/3" : "h-full bg-[#0055FF] w-3/4"}
        />
      </div>
    </div>
  );
}
